import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment as env } from '../../../environments/environment';
import { SoundPlayerService, SOUND_NOTES } from './sound-player.service';

/**
 * Per-customer error-report alert sound editor. Opens as a modal from
 * the Error Reports toolbar so the operator doesn't have to navigate
 * to Customer Directory + open each customer popup just to tune a
 * sound. Lists every customer (sorted, with the 'f2' company doc
 * surfaced as the "Default / non-customer reports" row at the top).
 *
 * Per-row controls: enable toggle, instrument dropdown, note dropdown,
 * preview button. Auto-saves 500ms after the last change via PUT
 * /admin/customer-directory/:id with a minimal {error_alert_sound}
 * body — the backend $sets only that field. Each save invalidates
 * SoundPlayerService's per-slug cache so subsequent live reports use
 * the updated tone immediately.
 *
 * Operator-level mute + volume stay in the Error Reports toolbar
 * (they're per-browser localStorage, not per-customer).
 */

interface CustomerRow {
  _id: string;
  name?: string;
  error_alert_sound?: { enabled: boolean; instrument: string; note: string };
  _saveState?: 'saving' | 'saved' | 'error' | null;
  _saveErr?: string;
}

@Component({
  selector: 'sound-settings-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center"
         (click)="onBackdropClick($event)">
      <div class="bg-app-black-100 border border-[#434863] rounded-lg shadow-2xl w-[860px] max-w-[95vw] max-h-[85vh] flex flex-col"
           (click)="$event.stopPropagation()">
        <div class="flex items-center justify-between p-4 border-b border-[#434863]">
          <h2 class="text-base font-semibold text-white">
            <i class="fa-solid fa-music mr-2 text-app-teal-100"></i>
            Error Report Sound Settings
          </h2>
          <button type="button" (click)="close.emit()"
                  class="text-gray-400 hover:text-white text-lg"
                  title="Close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="p-4 text-xs text-gray-400 border-b border-[#434863]">
          Per-customer alert tone played in the Error Reports live grid when a new
          report arrives. Operator-level mute + volume live in the toolbar.
          The <b class="text-white">f2 (Default / non-customer reports)</b> row covers reports
          submitted without a customer slug.
        </div>

        <div *ngIf="loading" class="p-8 text-center text-gray-400 text-sm">
          <i class="fa-solid fa-spinner fa-spin mr-2"></i> Loading customer list&hellip;
        </div>

        <div *ngIf="!loading && err" class="p-4 text-sm text-red-400">
          {{ err }}
        </div>

        <div *ngIf="!loading && !err" class="flex-1 overflow-y-auto p-4 space-y-2">
          <div *ngFor="let c of customers"
               class="flex items-center gap-3 border border-[#434863] rounded px-3 py-2 hover:bg-app-black-200/40">
            <div class="flex-1 min-w-0">
              <div class="text-sm text-white truncate">
                {{ c.name || c._id }}
                <span *ngIf="c._id === 'f2'" class="ml-1 text-[10px] uppercase tracking-wide text-amber-400">default</span>
              </div>
              <div class="text-[10px] text-gray-500 truncate font-mono">{{ c._id }}</div>
            </div>

            <label class="text-xs text-gray-300 flex items-center gap-1.5 cursor-pointer select-none">
              <input type="checkbox"
                     [checked]="c.error_alert_sound?.enabled === true"
                     (change)="onEnabledChange(c, $any($event.target).checked)" />
              Enabled
            </label>

            <select [ngModel]="c.error_alert_sound?.instrument || 'acoustic_grand_piano'"
                    (ngModelChange)="onInstrumentChange(c, $event)"
                    class="bg-app-black-200 border border-[#434863] text-gray-200 text-xs px-2 py-1 rounded"
                    [disabled]="c.error_alert_sound?.enabled !== true">
              <option value="acoustic_grand_piano">Piano</option>
              <option value="tubular_bells">Tubular bells</option>
              <option value="marimba">Marimba</option>
              <option value="xylophone">Xylophone</option>
              <option value="glockenspiel">Glockenspiel</option>
              <option value="vibraphone">Vibraphone</option>
              <option value="celesta">Celesta</option>
              <option value="music_box">Music box</option>
              <option value="kalimba">Kalimba</option>
              <option value="steel_drums">Steel drums</option>
              <option value="church_organ">Church organ</option>
              <option value="harpsichord">Harpsichord</option>
            </select>

            <select [ngModel]="c.error_alert_sound?.note || 'C5'"
                    (ngModelChange)="onNoteChange(c, $event)"
                    class="bg-app-black-200 border border-[#434863] text-gray-200 text-xs px-2 py-1 rounded w-20"
                    [disabled]="c.error_alert_sound?.enabled !== true">
              <option *ngFor="let n of soundNotes" [value]="n">{{ n }}</option>
            </select>

            <button type="button"
                    (click)="preview(c)"
                    class="text-xs px-2 py-1 border border-[#434863] rounded text-gray-300 hover:bg-app-black-200"
                    title="Preview this sound">
              <i class="fa-solid fa-volume-high"></i>
            </button>

            <div class="w-12 text-right">
              <span *ngIf="c._saveState === 'saving'" class="text-[10px] text-gray-400">Saving&hellip;</span>
              <span *ngIf="c._saveState === 'saved'" class="text-[10px] text-green-400">✓ Saved</span>
              <span *ngIf="c._saveState === 'error'"
                    class="text-[10px] text-red-400"
                    [title]="c._saveErr || 'Save failed'">
                ✕ Failed
              </span>
            </div>
          </div>
        </div>

        <div class="p-3 border-t border-[#434863] flex justify-between items-center text-[10px] text-gray-500">
          <div>Changes auto-save 500ms after the last edit.</div>
          <button type="button" (click)="close.emit()" class="dark-btn">
            Done
          </button>
        </div>
      </div>
    </div>
  `,
})
export class SoundSettingsDialogComponent implements OnInit {
  @Output() close = new EventEmitter<void>();

  customers: CustomerRow[] = [];
  loading = true;
  err: string | null = null;
  readonly soundNotes = SOUND_NOTES;

  private _saveDebounce: Map<string, any> = new Map();

  constructor(private http: HttpClient, private sound: SoundPlayerService) {}

  async ngOnInit() {
    await this.load();
  }

  async load() {
    this.loading = true;
    this.err = null;
    try {
      const r: any = await this.http.get(
        `${env.apiUrl}/rest/admin/customer-directory/`,
        { withCredentials: true },
      ).toPromise();
      const list: CustomerRow[] = Array.isArray(r) ? r : [];
      // Sort: 'f2' first (it's the default/non-customer fallback), rest by name.
      list.sort((a, b) => {
        if (a._id === 'f2') return -1;
        if (b._id === 'f2') return 1;
        const an = (a.name || a._id || '').toLowerCase();
        const bn = (b.name || b._id || '').toLowerCase();
        return an.localeCompare(bn);
      });
      this.customers = list;
    } catch (e: any) {
      this.err = e?.error?.message || e?.message || 'Failed to load customers';
      this.customers = [];
    }
    this.loading = false;
  }

  onBackdropClick(_e: MouseEvent) {
    this.close.emit();
  }

  _ensureSound(c: CustomerRow) {
    if (!c.error_alert_sound) {
      c.error_alert_sound = { enabled: false, instrument: 'acoustic_grand_piano', note: 'C5' };
    }
  }

  onEnabledChange(c: CustomerRow, v: boolean) {
    this._ensureSound(c);
    c.error_alert_sound!.enabled = v;
    this._save(c);
  }
  onInstrumentChange(c: CustomerRow, v: string) {
    this._ensureSound(c);
    c.error_alert_sound!.instrument = v;
    this._save(c);
  }
  onNoteChange(c: CustomerRow, v: string) {
    this._ensureSound(c);
    c.error_alert_sound!.note = v;
    this._save(c);
  }

  preview(c: CustomerRow) {
    this._ensureSound(c);
    this.sound.preview(
      c.error_alert_sound!.instrument || 'acoustic_grand_piano',
      c.error_alert_sound!.note || 'C5',
    );
  }

  private _save(c: CustomerRow) {
    const existing = this._saveDebounce.get(c._id);
    if (existing) clearTimeout(existing);
    const t = setTimeout(async () => {
      this._saveDebounce.delete(c._id);
      c._saveState = 'saving';
      try {
        // Minimal PUT body — backend $sets only the supplied fields, so
        // sending just error_alert_sound updates that field surgically
        // without disturbing the rest of the customer doc.
        await this.http.put(
          `${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(c._id)}`,
          { error_alert_sound: c.error_alert_sound },
          { withCredentials: true },
        ).toPromise();
        c._saveState = 'saved';
        // Invalidate the local-cache so next playForCustomer for this slug
        // refetches from brand-config and gets the updated tone.
        this.sound.invalidateCustomer(c._id);
        // Clear the saved indicator after 2s.
        setTimeout(() => { if (c._saveState === 'saved') c._saveState = null; }, 2000);
      } catch (e: any) {
        c._saveState = 'error';
        c._saveErr = e?.error?.message || e?.message || 'save failed';
      }
    }, 500);
    this._saveDebounce.set(c._id, t);
  }
}
